/**
 * DashboardFilterBar: date range (presets or custom days) and a campaign
 * multi-select. Every widget below it reads the same filters from the URL.
 */

import { CalendarRange, ChevronDown, Megaphone, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useCampaigns } from '@/features/campaigns/use-campaigns'
import { localDateStamp } from '@/features/leads/export-helpers'
import { DEFAULT_FILTERS, RANGE_PRESETS, rangeLabel, resolveRange, type RangePreset } from './filters'
import { useDashboardFilters } from './use-dashboard-filters'

export function DashboardFilterBar() {
  const { filters, setFilters, filtered } = useDashboardFilters()
  const { data: campaigns = [] } = useCampaigns()
  const today = localDateStamp(new Date())

  const selected = new Set(filters.campaignIds)
  const campaignLabel =
    selected.size === 0
      ? 'All campaigns'
      : selected.size === 1
        ? (campaigns.find((c) => selected.has(c.id))?.name ?? '1 campaign')
        : `${selected.size} campaigns`

  const setRange = (range: RangePreset) => {
    if (range !== 'custom') return setFilters({ ...filters, range, from: '', to: '' })
    // Start a custom range from what is on screen now (or the last 30 days).
    const shown = resolveRange(filters.range === 'all' ? { ...filters, range: '30d' } : filters)
    setFilters({
      ...filters,
      range,
      from: shown.from ? localDateStamp(shown.from) : '',
      to: shown.to ? localDateStamp(new Date(shown.to.getTime() - 1)) : today,
    })
  }

  const toggleCampaign = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setFilters({ ...filters, campaignIds: [...next] })
  }

  return (
    <div className="flex flex-wrap items-center gap-2" data-tour="dashboard-filters">
      <Select value={filters.range} onValueChange={(v) => setRange(v as RangePreset)}>
        <SelectTrigger className="h-8 w-44 text-xs" aria-label="Date range">
          <div className="flex items-center gap-1.5">
            <CalendarRange className="h-3.5 w-3.5" />
            <SelectValue />
          </div>
        </SelectTrigger>
        <SelectContent>
          {RANGE_PRESETS.map((p) => (
            <SelectItem key={p.value} value={p.value} className="text-xs">
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {filters.range === 'custom' && (
        <div className="flex items-center gap-1.5">
          <Input
            type="date"
            aria-label="From"
            value={filters.from}
            max={filters.to || today}
            onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            className="h-8 w-36 text-xs"
          />
          <span className="text-xs text-muted-foreground">to</span>
          <Input
            type="date"
            aria-label="To"
            value={filters.to}
            min={filters.from || undefined}
            max={today}
            onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            className="h-8 w-36 text-xs"
          />
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="max-w-56" aria-label="Campaigns">
            <Megaphone className="h-3.5 w-3.5" />
            <span className="truncate">{campaignLabel}</span>
            <ChevronDown className="h-3.5 w-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
          <DropdownMenuLabel className="text-xs">Campaigns</DropdownMenuLabel>
          <DropdownMenuItem
            className="text-xs"
            disabled={selected.size === 0}
            onSelect={(e) => {
              e.preventDefault()
              setFilters({ ...filters, campaignIds: [] })
            }}
          >
            All campaigns
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {campaigns.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No campaigns yet.</p>}
          {campaigns.map((c) => (
            <DropdownMenuCheckboxItem
              key={c.id}
              className="text-xs"
              checked={selected.has(c.id)}
              // Keep the menu open so several campaigns can be ticked.
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => toggleCampaign(c.id)}
            >
              <span className="truncate">{c.name}</span>
              {c.status !== 'active' && <span className="ml-auto pl-2 text-[10px] text-muted-foreground">{c.status}</span>}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {filtered && (
        <Button variant="ghost" size="sm" onClick={() => setFilters(DEFAULT_FILTERS)}>
          <X className="h-3.5 w-3.5" />
          Clear
        </Button>
      )}

      {filtered && (
        <p className="w-full text-xs text-muted-foreground">
          {filters.range === 'all' ? 'All time' : rangeLabel(filters)}
          {selected.size > 0 && ` · ${campaignLabel}`}. Lead numbers count leads added in these dates; message numbers count
          messages sent in them.
        </p>
      )}
    </div>
  )
}
